import React, { useEffect, useState } from 'react';
import axios from 'axios';
import serverURL from '../config';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import editImg from '../assets/pencil.png';
import deleteImg from '../assets/delete.png';
import EventModal from '../components/EventModal';
import { Tooltip } from "react-tooltip";
import 'react-tooltip/dist/react-tooltip.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FilterButton from '../components/FilterButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faUsers, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const EventList = () => {
    const [events, setEvents] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [eventTitle, setEventTitle] = useState('');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [filterStatus, setFilterStatus] = useState('active');
    const navigate = useNavigate();

    useEffect(() => {
        let isMounted = true;

        const fetchEvents = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await axios.get(`${serverURL}/events`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (isMounted) {
                    setEvents(response.data);
                }
            } catch (error) {
                console.error('Failed to fetch events:', error);
            }
        };

        fetchEvents();

        return () => {
            isMounted = false;
        };
    }, []);

    // Standalone fetchEvents for manual refresh
    const fetchEvents = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${serverURL}/events`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEvents(response.data);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    const openEventModal = () => {
        setModalOpen(true);
    };

    const closeEventModal = () => {
        setModalOpen(false);
        setSelectedEvent(null);
    };

    const handleAdd = () => {
        openEventModal();
        setEventTitle('Add Event');
    };

    const handleEdit = (event) => {
        setSelectedEvent(event);
        openEventModal();
        setEventTitle('Edit Event');
    };

    const handleDelete = (event) => {
        setSelectedEvent(event);
        openEventModal();
        setEventTitle('Delete Event');
    };

    const filteredEvents = events.filter(event => {
        if (filterStatus === 'all') return true;
        if (filterStatus === 'active') return event.status !== 'completed';
        if (filterStatus === 'expired') return event.status === 'completed';
        return true;
    });

    return (
        <div className="flex flex-col min-h-screen">
            <Header headerTitle={"vTest"} />
            <main className="flex-grow p-6">
                <div className="flex justify-between mb-4">
                    <h1 className="text-2xl font-bold">Events List</h1>
                    <div className='flex gap-3'>
                        <div className="flex gap-1 border-2 border-black rounded-full p-1 bg-white shadow-sm">
                            <FilterButton
                                label="All"
                                value="all"
                                currentFilter={filterStatus}
                                onClick={setFilterStatus}
                            />
                            <FilterButton
                                label="Active"
                                value="active"
                                currentFilter={filterStatus}
                                onClick={setFilterStatus}
                            />
                            <FilterButton
                                label="Expired"
                                value="expired"
                                currentFilter={filterStatus}
                                onClick={setFilterStatus}
                            />
                        </div>

                        <button
                            onClick={() => navigate('/categories')}
                            className="bg-gray-800 hover:bg-red-500 text-white p-2 rounded"
                        >
                            Categories
                        </button>

                        <button
                            onClick={handleAdd}
                            className="bg-gray-800 hover:bg-red-500 text-white p-2 rounded"
                        >
                            Add Event
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredEvents.length === 0 && (
                        <p>No events found.</p>
                    )}
                    {filteredEvents.map((event) => (
                        <div
                            key={event.id}
                            className="bg-white p-4 rounded shadow-xl border hover:shadow-xl transition-shadow duration-300 relative"
                        >
                            <div className='flex justify-end gap-3'>
                                <div className={`flex items-center text-white text-xs font-bold py-1 px-2 rounded ${event.status === 'completed' ? 'bg-red-600' : 'bg-green-600'
                                    }`}>
                                    {event.status === 'completed' ? 'EXPIRED' : 'ACTIVE'}
                                </div>
                            </div>
                            <h3 className="text-lg font-semibold mt-3">{event.name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                                <p className="text-gray-600">Institution: {event.institution}</p>
                                <div className="flex items-center bg-gray-200 text-black text-xs font-bold py-1 px-2 rounded">
                                    <FontAwesomeIcon icon={faUsers} className="mr-1" />
                                    {event.candidate_count} Candidates
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                                <p className="text-gray-600">Date: {new Date(event.event_date).toLocaleDateString()}</p>
                                <div className="flex items-center bg-gray-200 text-black text-xs font-bold py-1 px-2 rounded">
                                    <FontAwesomeIcon icon={faClock} className="mr-1" />
                                    {event.duration} min
                                </div>
                            </div>

                            <div className='flex justify-between mt-2'>
                                <button
                                    onClick={() =>
                                        navigate(`/event/${event.id}/${encodeURIComponent(event.name)}`)
                                    }
                                    className="bg-gray-800 hover:bg-red-500 text-white p-2 rounded"
                                >
                                    View Details
                                </button>
                                <div className='flex justify-between gap-3'>
                                    <button data-tooltip-id="tooltip-edit" className='bg-gray-200 py-2 px-3 hover:bg-gray-300 rounded' onClick={() => handleEdit(event)}>
                                        <img src={editImg} width={'15px'} height={'15px'} alt='editIcon' />
                                    </button>

                                    <button data-tooltip-id="tooltip-delete" className='bg-gray-200 py-2 px-3 hover:bg-gray-300 rounded' onClick={() => handleDelete(event)}>
                                        <img src={deleteImg} width={'15px'} height={'15px'} alt='deleteIcon' />
                                    </button>
                                    <Tooltip
                                        id="tooltip-edit"
                                        content={"Edit"}
                                        place="top"
                                    />
                                    <Tooltip
                                        id="tooltip-delete"
                                        content={"Delete"}
                                        place="top"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </main>
            <Footer />

            {modalOpen && (
                <EventModal title={eventTitle} event={selectedEvent} closeEventModal={closeEventModal} fetchEvents={fetchEvents} />
            )}
        </div>
    );
};

export default EventList;